import { Form, Formik } from "formik";
import { ClassicInputGroup, FieldType, InputGroup } from "@typedefs/modulesInterfaces";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import * as Yup from "yup";
import TranslatableText from "@components/translations/TranslatableText";
import ModuleButton from "@components/button/ModuleButton";
import { useState, useRef, useEffect, useMemo } from "react";
import FadeAnimation from "../../animations/FadeAnimation";
import { useGetAppLanguage } from "../../../app/hooks";
import { useGetClimateTypesQuery, useGetMoistureTypesQuery } from "@hooks/index";
import { Option } from "@typedefs/modulesInterfaces";

export interface ClimateIdentifiers {
  mat: number | null, //mean annual temperature
  map: number | null,//mean annual precipitation
  pet: number | null, //potential evapotranspiration 
  elevation: number | null //elevation above the sea
}

const meanAnnualTemperature: ClassicInputGroup = {
  type: FieldType.CLASSIC,
  inputName: "mat",
  inputType: "number",
  label: "map.mat",
  unit: "[C]"
};

const meanAnnualPrecipitation: ClassicInputGroup = {
  type: FieldType.CLASSIC,
  inputName: "map",
  inputType: "number",
  label: "map.map",
  unit: "[mm]"
};

const potentialEvapotranspiration: ClassicInputGroup = {
  type: FieldType.CLASSIC,
  inputName: "pet",
  inputType: "number",
  label: "map.pet",
  unit: "[mm]"
};

const elevation: ClassicInputGroup = {
  type: FieldType.CLASSIC,
  inputName: "elevation",
  inputType: "number",
  label: "map.elevation",
};

const inputGroups: InputGroup[] = [
  meanAnnualTemperature,
  meanAnnualPrecipitation,
  potentialEvapotranspiration,
  elevation
]

const validationSchema = Yup.object().shape({
  mat: Yup.number().nullable().required('validations.mat_required'),
  map: Yup.number().nullable().required('validations.map_required').min(0, 'validations.field_cant_be_negative'),
  pet: Yup.number().nullable().required('validations.pet_required').min(0, 'validations.field_cant_be_negative'),
  elevation: Yup.number().nullable().required('validations.elevation_required').min(0, 'validations.field_cant_be_negative')
});

const calculateClimate = ({ mat, elevation }: ClimateIdentifiers) => {
  if (mat === null || elevation === null) return undefined;

  if (mat > 18) return elevation < 1000 ? "Tropical" : "Tropical Montane";
  if (mat > 10) return "Warm Temperate"; // 10 < mat <= 18
  if (mat > 0) return "Cool Temperate"; // 0 < mat <= 10
  return "Boreal"; // mat <= 0
  // no polar
}

const calculateMoisture = ({ mat, map, pet, elevation }: ClimateIdentifiers) => {
  if (mat === null || elevation === null || map === null || pet === null) return undefined;

  if (mat <= 18) return map > pet ? "Moist" : "Dry"; // Moist if map > pet, Dry otherwise
  if (elevation >= 1000) return "Dry"; // mat > 18
  return map >= 2000 ? "Wet" : map >= 1000 ? "Moist" : "Dry"; // mat > 18 && elevation < 1000
}


const ClimateIdentifier = ({ initialValues }: { initialValues: ClimateIdentifiers }) => {
  const [climate, setClimate] = useState<string>();
  const [moisture, setMoisture] = useState<string>();
  const { data: climateTypes } = useGetClimateTypesQuery();
  const { data: moistureTypes } = useGetMoistureTypesQuery();
  const { apiLanguageField } = useGetAppLanguage();

  // Add ref for scrolling
  const componentRef = useRef<HTMLDivElement>(null);

  // Check initialValues and scroll when all are truthy
  useEffect(() => {
    const allValuesPresent = Object.values(initialValues).every(value => !!value);
    if (allValuesPresent && componentRef.current) {
      componentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [initialValues]);

  const handleSubmit = (values: ClimateIdentifiers) => {
    // Ensure values are numbers (Formik may return strings for number inputs)
    const numericValues: ClimateIdentifiers = {
      mat: values.mat !== null ? Number(values.mat) : null,
      map: values.map !== null ? Number(values.map) : null,
      pet: values.pet !== null ? Number(values.pet) : null,
      elevation: values.elevation !== null ? Number(values.elevation) : null,
    };
    setClimate(calculateClimate(numericValues))
    setMoisture(calculateMoisture(numericValues))
  }

  const localizedClimateName = useMemo(() => {
    return climateTypes?.find(c => c.name_en === climate)?.[apiLanguageField as keyof Option] as string
  }, [climate, climateTypes, apiLanguageField])
  const localizedMoistureName = useMemo(() => {
    return moistureTypes?.find(m => m.name_en === moisture)?.[apiLanguageField as keyof Option] as string
  }, [moisture, moistureTypes, apiLanguageField])

  return (
    <div ref={componentRef}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
        <Form className="pt-2">
          <h3>
            <TranslatableText translationKey="map.climate_identifier" className="ff-medium" />
          </h3>

          {inputGroups.map((input) => (
            <div className="py-1" key={input.label}>
              <GenericInputGroup
                inputGroup={{ ...input }}
                showCommentIcon={false}
              />
            </div>
          ))}

          <div className="w-100 d-flex justify-content-end">
            <ModuleButton labelKey="map.calculate" />
          </div>
        </Form>
      </Formik>

      <FadeAnimation isShown={!!climate || !!moisture}>
        <div className="pt-2">
          <TranslatableText translationKey="main.climate" />: {localizedClimateName}
        </div>
        <div className="pt-2">
          <TranslatableText translationKey="main.moisture" />: {localizedMoistureName}
        </div>
      </FadeAnimation>
    </div>

  );
}

export default ClimateIdentifier;